import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button } from "@/components/common/Button";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { Tooltip } from "@/components/common/Tooltip";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import type { ProviderCatalog } from "@/lib/api/apps/types";
import {
  payerLine,
  trialLine,
  usableProviders,
  type ModelChoice,
  type OrgRef,
} from "./draft-model";

/** The active organization, as the model chips name who pays. */
export function useActiveOrgRef(): OrgRef {
  const org = useOrgContextStore(
    useShallow((s) => s.orgs.find((o) => o.org_id === s.activeOrgId)),
  );
  return {
    name: org?.name ?? "The organization",
    personal: org?.is_personal ?? false,
  };
}

const groupLabel: React.CSSProperties = {
  fontSize: "var(--font-size-xx-small)",
  letterSpacing: "0.1em",
};

/** The model a draft runs on, as a chip: the model and, given the org, who
 *  pays; its menu lists only what the organization can pay for. */
export const ModelChip: React.FC<{
  catalog: ProviderCatalog;
  /** Adds the payer line beside the chip. */
  org?: OrgRef;
  choice: ModelChoice;
  onPick: (provider: string, model: string) => void;
  menuOpen: boolean;
  onMenuOpen: (open: boolean) => void;
  tooltip?: string;
}> = ({ catalog, org, choice, onPick, menuOpen, onMenuOpen, tooltip }) => {
  const anchor = React.useRef<HTMLButtonElement>(null);
  const chip = (
    // eslint-disable-next-line no-restricted-syntax -- a chip that opens the model menu
    <button
      ref={anchor}
      type="button"
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      onClick={() => onMenuOpen(!menuOpen)}
      className={`menu-trigger-sm w-auto min-w-0 shrink justify-start gap-1.5 hover:bg-bg-hover ${menuOpen ? "border-accent" : ""}`}
    >
      <AutoAwesomeOutlinedIcon
        sx={{ fontSize: 14 }}
        className="text-fg-muted"
      />
      <span className="truncate">{choice.model.name}</span>
      <ExpandMoreIcon sx={{ fontSize: 16 }} className="text-fg-muted" />
    </button>
  );
  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip}
      {org && (
        <span className="min-w-0 truncate text-mono-small-muted">
          {payerLine(choice, org)}
        </span>
      )}
      <ModelMenu
        anchorRef={anchor}
        isOpen={menuOpen}
        onClose={() => onMenuOpen(false)}
        catalog={catalog}
        choice={choice}
        onPick={onPick}
      />
    </>
  );
};

/** Only while the organization has no model key of its own: where one is
 *  added, for whoever may add it. */
export const NoKeysLine: React.FC<{
  catalog: ProviderCatalog;
  orgId: string;
}> = ({ catalog, orgId }) => {
  const navigate = useNavigate();
  if (catalog.providers.some((p) => p.needs_key && p.key === "on_file"))
    return null;
  return catalog.can_manage_keys ? (
    <Button
      variant="quiet-link"
      size="xs"
      className="self-start"
      onClick={() => navigate(`/organizations/${orgId}#models`)}
    >
      or use your own API key
      <ArrowForwardIcon className="icon-xs" />
    </Button>
  ) : (
    <span className="text-fg-muted-regular-small">
      Your organization has not set up any model keys.
    </span>
  );
};

/** The models a draft can run on, by provider, with what one draft costs. */
export const ModelMenu: React.FC<{
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onClose: () => void;
  catalog: ProviderCatalog;
  choice: ModelChoice;
  onPick: (provider: string, model: string) => void;
}> = ({ anchorRef, isOpen, onClose, catalog, choice, onPick }) => {
  const usable = usableProviders(catalog);
  return (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={onClose}
      label="Model"
      className="py-1"
      style={{ minWidth: "22rem" }}
    >
      {usable.map((p) => (
        <React.Fragment key={p.id}>
          <span
            className="flex items-center gap-2 px-2 pt-2 pb-1 font-mono text-fg-muted uppercase select-none"
            style={groupLabel}
          >
            <span>{p.name}</span>
            <span className="ml-auto normal-case">
              {p.key === "house" ? `Trial · ${trialLine(p)}` : `key ${p.hint}`}
            </span>
          </span>
          {p.models.map((m) => {
            const selected =
              choice.provider.id === p.id && choice.model.id === m.id;
            return (
              <DropdownMenuItem
                key={m.id}
                isSelected={selected}
                onClick={() => {
                  onPick(p.id, m.id);
                  onClose();
                }}
              >
                <span className="flex w-full min-w-0 items-center gap-2">
                  <span className="truncate text-left">{m.name}</span>
                  <span className="ml-auto flex w-3.5 justify-center">
                    {selected && (
                      <CheckIcon
                        sx={{ fontSize: 14 }}
                        className="text-accent"
                      />
                    )}
                  </span>
                </span>
              </DropdownMenuItem>
            );
          })}
        </React.Fragment>
      ))}
    </DropdownMenu>
  );
};
